// Tipos do módulo Portal de Cotações (backend próprio, porta 8082).
// Espelham os shapes de docs/portal-cotacoes/modelo-de-dados.md.

export type CotacaoStatus =
  | 'RASCUNHO'
  | 'ENVIADA'
  | 'EM_RESPOSTA'
  | 'EM_DECISAO'
  | 'AGUARDANDO_APROVACAO'
  | 'GERANDO_PEDIDOS'
  | 'CONCLUIDA'
  | 'CANCELADA';

export type ConviteStatus =
  | 'CRIADO'
  | 'ENVIADO'
  | 'VISUALIZADO'
  | 'RESPONDIDO'
  | 'DECLINADO'
  | 'REVOGADO'
  | 'BLOQUEADO_REVISAO';

export type CanalDisparo = 'EMAIL' | 'WHATSAPP';

export type MotivoSemCompra = 'SEM_PROPOSTA' | 'DESCARTADO';

/** Rótulo em português do motivo de não-compra — enum cru nunca vai para a tela. */
export const MOTIVO_SEM_COMPRA_LABEL: Record<string, string> = {
  SEM_PROPOSTA: 'nenhum fornecedor cotou',
  DESCARTADO: 'descartado pelo comprador',
};

export interface SolicitacaoItem {
  codigoProduto: string;
  descricao: string;
  /**
   * Quantidade A COTAR. Em complemento com quantidade aumentada é o **acréscimo** — a
   * origem já resolve isso (o resto foi solicitado antes); a tela não recalcula.
   */
  quantidade: number;
  unidade: string;
  /**
   * Contexto que o FORNECEDOR lê (ex.: "Quantidade adicional do 2º complemento: +2 …").
   * Opcional: só a origem que conhece complemento produz.
   */
  observacao?: string | null;
  /**
   * Referências de preço do orçamento de origem, para o COMPRADOR: valor orçado no Cilia e
   * média ponderada das compras no ERP (+ nº de compras da janela). Opcionais — sementes e
   * o futuro provider do Nasajon não as informam.
   * ⚠️ **Nunca exibir na tela pública do fornecedor** (`views/cotacao_publica`).
   */
  valorOrcado?: number | null;
  mediaCompras?: number | null;
  mediaComprasQtd?: number | null;
  /** Preço online da peça (orcamento-cilia/at-15) — ausente quando ela não foi pesquisada. */
  precoOnline?: number | null;
  precoOnlineCanal?: string | null;
  precoOnlineUrl?: string | null;
  precoOnlinePesquisadoEm?: string | null;
  precoOnlineConferir?: boolean | null;
}

export interface Solicitacao {
  numero: string;
  placa: string | null;
  veiculo: string | null;
  /** Chassi e local de entrega (at-17), vindos do módulo de Requisições. */
  chassi?: string | null;
  localEntrega?: { nome: string | null; endereco: string | null } | null;
  aprovadaEm: string;
  /**
   * Origem do orçamento em texto pronto ("Inicial", "2º complemento"), exibida ao lado do
   * número no card do wizard. **Opcional**: só o simulador do Orçamento Cilia produz — as
   * sementes e o futuro provider do ERP não têm o conceito de complemento (12/08/2026).
   */
  origem?: string;
  itens: SolicitacaoItem[];
}

export interface SolicitacoesResponse {
  data: Solicitacao[];
}

export interface FornecedorContato {
  id: string;
  nome: string | null;
  telefone: string | null;
  email: string | null;
  principal: boolean;
}

export interface Fornecedor {
  cnpj: string;
  razaoSocial: string;
  codigoErp: string | null;
  inativo: boolean;
  notaQualidade: string | number | null;
  contatos: FornecedorContato[];
}

export interface Preset {
  id: string;
  nome: string;
  descricao: string | null;
  pesoPreco: number;
  pesoPrazo: number;
  pesoQualidade: number;
  ativo: boolean;
}

export interface CotacaoListItem {
  id: string;
  numero: string;
  solicitacaoNumero: string;
  placa: string | null;
  veiculo: string | null;
  compradorNome: string | null;
  status: CotacaoStatus;
  prazoRespostaAte: string;
  createdAt: string;
  totalItens: number;
  respostas: { responderam: number; convidados: number };
}

/**
 * Itens de uma cotação, como vêm do lote (`GET /cotacoes/itens-em-lote`) — 13/08/2026.
 *
 * Existe porque a listagem devolve só `totalItens`: sem estes itens, o aviso de repetição
 * do wizard só conseguia falar da CADEIA ("este orçamento já tem cotação em outra versão"),
 * nunca de QUAL peça, e a conferência do Cilia não conseguia cruzar peça removida com
 * cotação aberta.
 */
export interface CotacaoItensLote {
  cotacaoId: string;
  numero: string;
  status: CotacaoStatus;
  solicitacaoNumero: string;
  itens: { codigoProduto: string; descricao: string; quantidade: number; unidade: string }[];
}

// ── Supervisão do fluxo (item 9 do backlog, 13/08/2026) ───────────────────────

export interface SupervisaoLinha {
  id: string;
  numero: string;
  status: CotacaoStatus;
  compradorNome: string | null;
  placa: string | null;
  veiculo: string | null;
  solicitacaoNumero: string;
  criadaEm: string;
  enviadaEm: string | null;
  entrouEmDecisaoEm: string | null;
  prazoRespostaAte: string;
  respostas: { responderam: number; convidados: number };
  /** Dias corridos desde o marco da etapa atual — calculado no backend, um lugar só. */
  diasNaEtapaAtual: number;
  /**
   * Quantos dias se espera que a etapa atual dure (at-11) — `null` = etapa não vigiada, seja
   * porque o ADMIN a tirou da configuração, seja porque ela tem relógio próprio (o prazo de
   * resposta). A tela **não** inventa limiar quando vem `null`.
   */
  limiarDias?: number | null;
  /** Só alerta enquanto a cotação espera resposta (depois disso, prazo passado é o normal). */
  prazoVencido: boolean;
}

/**
 * Cotação FINALIZADA — o que aconteceu com ela (14/08/2026, pedido do Victor).
 *
 * É resumo de desfecho, não histórico: o rastreamento completo ("literalmente tudo") é a
 * timeline da Auditoria de Cotações, para onde a linha aponta. Duplicar aqui criaria duas
 * versões da mesma história, que divergem na primeira mudança de regra.
 */
export interface SupervisaoFinalizadaLinha {
  id: string;
  numero: string;
  /** Sempre `CONCLUIDA` ou `CANCELADA` — os dois estados terminais. */
  status: CotacaoStatus;
  placa: string | null;
  veiculo: string | null;
  compradorNome: string | null;
  criadaEm: string;
  /** `concluidaEm` ou `canceladaEm`. `null` só em registro antigo sem o marco gravado. */
  finalizadaEm: string | null;
  cancelamentoMotivo: string | null;
  fornecedoresConvidados: number;
  /**
   * `null` = a cotação NUNCA precisou do gestor (tudo no menor preço) — distinto de
   * `{ aprovadas: 0, reprovadas: 0 }`, que seria "precisou e nada foi decidido".
   */
  gestor: { aprovadas: number; reprovadas: number; ultimaEm: string | null } | null;
  /** Quem aparece no histórico da cotação — "quem passou a mão nela". */
  autores: string[];
}

export interface SupervisaoFinalizadasResposta {
  data: SupervisaoFinalizadaLinha[];
  total: number;
}

export interface SupervisaoResposta {
  emCurso: SupervisaoLinha[];
  resumo: {
    porStatus: Partial<Record<CotacaoStatus, number>>;
    /** `null` = ainda não há amostra do intervalo. A tela mostra "—", nunca 0. */
    temposMediosDias: {
      rascunhoAteEnvio: number | null;
      envioAteDecisao: number | null;
      decisaoAteConclusao: number | null;
    };
  };
}

/**
 * Auditoria de cotações (at-05, 14/08/2026). A timeline funde três fontes do backend do
 * Portal — ações internas (`audit_logs`), o lado do fornecedor (`convite_eventos`, com IP)
 * e os disparos de convite —, já humanizadas lá: a tela só renderiza.
 */
export interface AuditoriaCotacaoItem {
  id: string;
  numero: string;
  placa: string | null;
  veiculo: string | null;
  status: CotacaoStatus;
  createdAt: string;
}

export interface AuditoriaEvento {
  id: string;
  em: string;
  origem: 'INTERNO' | 'FORNECEDOR' | 'SISTEMA';
  /** Chave crua da ação/evento — a tela usa para ícone e cor; o texto já vem pronto. */
  tipo: string;
  titulo: string;
  /** Nulo quando o evento é do fornecedor ou do sistema. */
  autor: string | null;
  fornecedor: string | null;
  detalhes: string[];
  /**
   * O que o evento abre ao ser clicado, ou `null` para os que não abrem nada (a maioria).
   * União discriminada: o próximo evento clicável entra aqui sem somar flag ao contrato.
   */
  abrir: AberturaDoEvento | null;
}

export type AberturaDoEvento =
  /** Proposta **daquela versão**, mesmo que substituída depois. */
  | { tipo: 'PROPOSTA'; propostaId: string }
  /**
   * Fornecedores convidados. `quantidadeRegistrada` é a contagem que o log gravou no
   * momento do envio — o modal a compara com a lista atual e avisa se divergirem.
   */
  | { tipo: 'CONVITES'; quantidadeRegistrada: number | null }
  /**
   * Escolhas registradas NAQUELE salvamento — vêm dentro do próprio evento, porque estão
   * no log. `escolhas: null` = registro anterior a 14/08/2026, quando só a contagem era
   * gravada; a tela diz isso em vez de mostrar a decisão de hoje como se fosse a de então.
   */
  | { tipo: 'DECISOES'; escolhas: EscolhaRegistrada[] | null }
  /**
   * A cesta COMO FOI FECHADA — vem do log, porque as decisões mudam depois. `cesta: null`
   * = registro anterior a 14/08/2026, quando só o desfecho era gravado.
   */
  | { tipo: 'CESTA'; cesta: EscolhaRegistrada[] | null; total: number | null; resultado: string | null }
  /** Pedidos gerados: os dados vêm de rota, porque o pedido é entidade viva. */
  | { tipo: 'PEDIDOS' };

export interface AuditoriaPedidoItem {
  codigoProduto: string;
  /** Cai no código quando o item não casa com nenhum da cotação — nunca some da lista. */
  descricao: string;
  quantidade: number;
  unidade: string;
  valorUnitario: number | null;
  total: number | null;
  justificativa: string | null;
  foiMenorPreco: boolean | null;
}

export interface AuditoriaPedido {
  id: string;
  fornecedorNome: string;
  fornecedorCnpj: string;
  status: string;
  /** `true` = o ERP não executou de verdade (modo de simulação). */
  simulado: boolean;
  nasajonPedidoId: string | null;
  numeroExterno: string;
  previsaoDeEntrega: string | null;
  enviadoEm: string | null;
  /**
   * As chegadas registradas deste pedido (transversal/at-86). Substitui o `recebidoEm`
   * único: a chegada é por item e costuma ser parcial, então uma data só prometeria "o
   * pedido chegou" sobre uma entrega pela metade. Inclui as canceladas, marcadas.
   */
  recebimentos: AuditoriaPedidoRecebimento[];
  tentativas: number;
  ultimoErro: string | null;
  total: number | null;
  itens: AuditoriaPedidoItem[];
}

export interface AuditoriaPedidoRecebimento {
  id: string;
  descricao: string | null;
  quantidade: number;
  unidade: string | null;
  /** O dia em que a peça chegou (informado por quem marcou). */
  recebidoEm: string;
  registradoPor: string | null;
  /** O instante do clique — outro dado. */
  registradoEm: string;
  cancelado: boolean;
  cancelamentoMotivo: string | null;
}

export interface AuditoriaPedidos {
  cotacaoNumero: string;
  pedidos: AuditoriaPedido[];
}

export interface EscolhaRegistrada {
  item: string;
  fornecedor: string | null;
  valor: number | null;
  menorPreco: boolean;
  justificativa: string | null;
  /** Preenchido quando o item ficou sem compra — explica a ausência de vencedor. */
  semCompra: string | null;
}

/** Um convite da cotação, com o desfecho dele. */
export interface AuditoriaConvite {
  id: string;
  fornecedorNome: string;
  fornecedorCnpj: string;
  status: ConviteStatus;
  canais: CanalDisparo[];
  disparadoEm: string | null;
  primeiroAcessoEm: string | null;
  respondidoEm: string | null;
  /** Versão da proposta ativa; `null` quando o fornecedor não respondeu. */
  propostaVersao: number | null;
}

export interface AuditoriaConvites {
  cotacaoNumero: string;
  convites: AuditoriaConvite[];
}

/**
 * Quadro ATUAL das escolhas — o "como está agora", ao lado da linha do tempo, que responde
 * "o que mudou". Não é a foto de um instante: o histórico continua sendo a timeline.
 */
export interface AuditoriaEscolhaAtual {
  cotacaoItemId: string;
  descricao: string;
  codigoProduto: string;
  quantidade: number;
  unidade: string;
  /** `false` = item ainda SEM DECISÃO. Diferente de decidido como "sem compra". */
  decidido: boolean;
  fornecedor: string | null;
  valor: number | null;
  prazoEntregaDias: number | null;
  foiMenorPreco: boolean | null;
  justificativa: string | null;
  motivoSemCompra: MotivoSemCompra | null;
  decididoEm: string | null;
  menorPrecoDisponivel: { fornecedorNome: string; totalItem: number } | null;
  /**
   * Aprovação da decisão VIGENTE. `null` quando não há — inclusive quando existe uma
   * aprovação antiga referente a uma decisão que foi substituída (o backend a descarta
   * comparando `createdAt` com `decididoEm`).
   */
  statusAprovacao: 'PENDENTE' | 'APROVADA' | 'REPROVADA' | null;
  comentarioGestor: string | null;
  /**
   * `false` quando a escolha é o menor preço — não passa pelo gestor. É o que separa
   * "dispensada de aprovação" de "ainda não enviada ao gestor": sem ele, os dois casos
   * chegariam à tela como `statusAprovacao: null`.
   */
  requerAprovacao: boolean;
}

export interface AuditoriaEscolhasAtuais {
  cotacaoNumero: string;
  cotacaoStatus: CotacaoStatus;
  itens: AuditoriaEscolhaAtual[];
  totais: {
    escolhido: number;
    menorPreco: number;
    /** Quanto a cesta custa a mais que a soma dos menores preços. */
    diferenca: number;
    semDecisao: number;
    acimaDoMenorPreco: number;
  };
}

/** Proposta como o fornecedor enviou, na versão do evento auditado. */
export interface AuditoriaProposta {
  id: string;
  versao: number;
  /** `SUBSTITUIDA` = o fornecedor reenviou depois; a tela avisa. */
  status: 'ATIVA' | 'SUBSTITUIDA';
  enviadaEm: string;
  condicaoPagamento: string | null;
  observacao: string | null;
  cotacaoNumero: string;
  fornecedorNome: string;
  fornecedorCnpj: string;
  itens: {
    descricao: string;
    codigoProduto: string;
    quantidade: number;
    unidade: string;
    /** `false` = "não tenho esta peça" — o fornecedor fica fora do ranking do item. */
    cotou: boolean;
    precoUnitario: number | null;
    totalItem: number | null;
    prazoEntregaDias: number | null;
    marcaFabricante: string | null;
    observacaoItem: string | null;
  }[];
}

/** Cotação no card "Resumo Atual" — mais rica que o item da lista de busca. */
export interface AuditoriaCotacaoResumo extends AuditoriaCotacaoItem {
  compradorNome: string | null;
  solicitacaoNumero: string | null;
  prazoRespostaAte: string | null;
  _count: { convites: number };
}

export interface AuditoriaTimeline {
  cotacao: AuditoriaCotacaoResumo;
  eventos: AuditoriaEvento[];
  /** Autores presentes NA timeline — a lista do filtro, não o diretório de usuários. */
  autores: string[];
  tiposPresentes: string[];
}

export interface AuditoriaCotacoesBusca {
  data: AuditoriaCotacaoItem[];
  total: number;
}

export interface CotacaoListResponse {
  data: CotacaoListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CotacaoItem {
  id: string;
  ordem: number;
  codigoProduto: string;
  descricao: string;
  quantidade: string | number;
  unidade: string;
  observacao: string | null;
  /**
   * Referências de preço CONGELADAS na criação da cotação (12/08/2026): valor orçado no
   * Cilia e média de compras no ERP. Decimal do Prisma chega como string no JSON.
   * ⚠️ Uso interno do comprador — a tela pública do fornecedor não recebe estes campos.
   */
  valorOrcado?: string | number | null;
  mediaCompras?: string | number | null;
  mediaComprasQtd?: number | null;
  /** Preço online congelado na criação (orcamento-cilia/at-15). Decimal chega como string. */
  precoOnline?: string | number | null;
  precoOnlineCanal?: string | null;
  precoOnlineUrl?: string | null;
  precoOnlinePesquisadoEm?: string | null;
  precoOnlineConferir?: boolean | null;
  /** A decisão do comprador sobre o item (vem no detalhe). Sem vencedor = item sem compra. */
  decisao?: { propostaItemVencedorId: string | null; motivoSemCompra: MotivoSemCompra | null } | null;
}

/**
 * A proposta ATIVA que o fornecedor enviou, como chega dentro do convite no
 * `GET /cotacoes/:id`. Sempre esteve na resposta — a tela só passou a exibi-la em
 * 12/08/2026 (antes o comprador via "Respondeu em ..." e nenhum valor).
 * `precoUnitario` é Decimal do Prisma: chega como string no JSON.
 */
export interface PropostaItemRecebido {
  id: string;
  cotacaoItemId: string;
  /** Posição da oferta no item e a condição dela (at-16): o fornecedor pode oferecer a peça em várias. */
  variante?: number;
  condicao?: string | null;
  /** Fotos da peça, com URL assinada (at-16). */
  fotos?: { id: string; url: string | null }[];
  cotou: boolean;
  precoUnitario: string | number | null;
  prazoEntregaDias: number | null;
  marcaFabricante: string | null;
  observacaoItem: string | null;
}

export interface PropostaRecebida {
  id: string;
  versao: number;
  enviadaEm: string;
  condicaoPagamento: string | null;
  observacao: string | null;
  /** Formas de pagamento aceitas, marcadas pelo fornecedor (at-10). Vazio em proposta anterior. */
  formasPagamento?: string[];
  /** Frete da proposta inteira (at-11). `null`/ausente em proposta anterior ao campo. */
  frete?: number | null;
  itens: PropostaItemRecebido[];
}

export interface ConviteDetalhe {
  id: string;
  fornecedorCnpj: string;
  status: ConviteStatus;
  canais: CanalDisparo[];
  expiresAt: string;
  disparadoEm: string | null;
  primeiroAcessoEm: string | null;
  respondidoEm: string | null;
  fornecedor: Fornecedor;
  propostas: PropostaRecebida[];
  /**
   * O ÚLTIMO desfecho de envio por canal (at-07).
   *
   * ⚠️ Não confundir com `status` nem com `canais`: o `status` vira `ENVIADO` antes de os
   * disparos rodarem (e não volta atrás em falha), e `canais` é configuração. Só este campo
   * diz o que de fato aconteceu com o e-mail e com o WhatsApp.
   *
   * Opcional porque backend anterior à at-07 não devolve — a tela trata a ausência como "sem
   * registro de envio", que é o que ela é.
   */
  ultimosDisparos?: DisparoResumido[];
  /** Os itens que este fornecedor recebeu (at-18). Ausente em backend anterior: trate como todos. */
  cotacaoItemIds?: string[];
}

/** Último desfecho de UM canal, para a linha do fornecedor (at-07). */
export interface DisparoResumido {
  canal: CanalDisparo;
  sucesso: boolean;
  erro: string | null;
  /** `true` = só o link foi preparado; quem envia é a pessoa (`wa.me`). */
  manual: boolean;
  em: string;
  provedorMensagemId: string | null;
}

/**
 * Desfecho de UM canal de UM convite (12/08/2026). O backend sempre devolveu isto e a
 * tela jogava fora — "Convite reenviado" com o e-mail falhado por SMTP era falso sucesso.
 *
 * ⚠️ `manual` é o que distingue os dois WhatsApp, NUNCA o canal (at-07):
 * - `true` — o portal montou a URL `wa.me` e quem envia é o comprador, do próprio aparelho;
 *   `sucesso: true` aqui significa apenas "o link ficou pronto".
 * - `false` — o servidor enviou pelo número oficial da empresa (OctaDesk); `sucesso` reflete
 *   o que a plataforma respondeu, e falha traz `erro` com o motivo dela.
 */
export interface ResultadoDisparo {
  canal: CanalDisparo;
  destinatario: string;
  sucesso: boolean;
  manual: boolean;
  erro?: string | null;
  url?: string;
  /** Id da mensagem na plataforma (at-07) — chave do suporte no painel do OctaDesk. */
  provedorMensagemId?: string | null;
}

/**
 * Estado da integração de mensagens (at-07) — `GET /integracoes/octadesk` do Portal.
 *
 * São três booleanos porque as três perguntas são diferentes, e a tela precisa das três:
 * `configurada` (as credenciais existem neste ambiente), `ativa` (o switch da aba
 * Integrações) e `efetiva` — a única que diz o que o disparo VAI FAZER. Renderizar o botão
 * de envio automático por `ativa` prometeria um envio que não aconteceria.
 */
export interface StatusOctadesk {
  configurada: boolean;
  ativa: boolean;
  efetiva: boolean;
  templateConvite: string;
  variaveisEsperadas: string[];
}

/** Resposta do envio ativo por WhatsApp (at-07). */
export interface RespostaEnvioWhatsapp {
  ok: boolean;
  resultado: ResultadoDisparo;
  linkMudou: boolean;
}

/**
 * Resposta de Reenviar / Novo link.
 *
 * `linkMudou` diz o que aconteceu com o endereço que o fornecedor já tinha: **Novo link**
 * sempre rotaciona (é o que ele existe para fazer); **Reenviar** reaproveita o link vigente
 * desde 12/08/2026 (token guardado cifrado) e só rotaciona quando não há token recuperável
 * — convite criado antes dessa data. A tela precisa dizer qual dos dois foi, senão o
 * comprador fica sem saber o que continua valendo.
 */
export interface RespostaConvite {
  ok: boolean;
  link: string;
  linkMudou?: boolean;
  resultados: ResultadoDisparo[];
}

/**
 * Link VIGENTE do convite — leitura pura, não invalida nada (12/08/2026).
 *
 * `disponivel: false` = convite anterior ao token cifrado (ou chave trocada): o link antigo
 * segue funcionando para o fornecedor (a busca é pelo hash), mas não há como exibi-lo — a
 * saída é gerar um novo. O `prefixo` é o que permite casar um link relatado por telefone
 * com o convite, mesmo nesse caso.
 */
export interface LinkVigente {
  disponivel: boolean;
  link: string | null;
  expiresAt: string | null;
  prefixo: string | null;
  statusCotacao: CotacaoStatus;
}

/** Resposta do envio dos convites: o desfecho por convite/canal. */
export interface RespostaEnvio {
  cotacao: CotacaoDetalhe;
  disparos: { conviteId: string; resultados: ResultadoDisparo[] }[];
}

export interface CotacaoDetalhe {
  id: string;
  numero: string;
  solicitacaoNumero: string;
  placa: string | null;
  veiculo: string | null;
  /** Chassi e local de entrega da requisição (at-17). Só para o comprador. */
  chassi?: string | null;
  localEntregaNome?: string | null;
  localEntregaEndereco?: string | null;
  /** O "Motivo da Requisição" (`justificativa` do Nasajon), copiado na criação (at-17). Só para o comprador. */
  motivoRequisicao?: string | null;
  compradorNome: string | null;
  status: CotacaoStatus;
  prazoRespostaAte: string;
  createdAt: string;
  preset: Preset | null;
  pesosCongelados: { preco: number; prazo: number; qualidade: number } | null;
  itens: CotacaoItem[];
  convites: ConviteDetalhe[];
  pedidos: PedidoGerado[];
  /**
   * Itens que ainda não têm oferta, calculados no servidor (at-18). `null` fora de
   * ENVIADA/EM_RESPOSTA/EM_DECISAO (lista vazia ali seria lida como "tudo cotado"); ausente em
   * backend anterior.
   */
  itensSemOferta?: ItemSemOferta[] | null;
}

/** Um item que nenhum fornecedor ofertou ainda (at-18). */
export interface ItemSemOferta {
  cotacaoItemId: string;
  descricao: string;
  /** Quantos fornecedores receberam o item. */
  convidados: number;
  /** Quantos desses já responderam (sem ofertar o item). */
  responderam: number;
}

export interface PropostaComparada {
  propostaItemId: string;
  fornecedorCnpj: string;
  fornecedorNome: string;
  precoUnitario: number | null;
  totalItem: number;
  prazoEntregaDias: number;
  marcaFabricante: string | null;
  observacaoItem: string | null;
  /** Condição da oferta (at-16): o mesmo fornecedor pode oferecer a peça em mais de uma. */
  condicao?: string | null;
  /** Quantas fotos da peça o fornecedor mandou nesta oferta (at-16). */
  fotos?: number;
  score: number | null;
  flags: { menorPreco: boolean; melhorScore: boolean; propostaUnica: boolean };
}

export interface ItemComparado {
  cotacaoItemId: string;
  codigo: string;
  descricao: string;
  quantidade: number;
  unidade: string;
  propostaUnica: boolean;
  propostas: PropostaComparada[];
  naoCotaram: { fornecedorCnpj: string; fornecedorNome: string }[];
  semResposta: { fornecedorCnpj: string; fornecedorNome: string }[];
  /** Quem não recebeu este item (at-18). Não é "não cotou": nunca foi perguntado. */
  naoConvidados?: { fornecedorCnpj: string; fornecedorNome: string }[];
  decisao: {
    propostaItemVencedorId: string | null;
    motivoSemCompra: MotivoSemCompra | null;
    justificativa: string | null;
    foiMenorPreco: boolean;
    foiMelhorScore: boolean;
  } | null;
  /**
   * Reabertura parcial (13/08/2026): depois que a cesta foi fechada uma vez, só o item
   * REPROVADO pelo gestor volta a aceitar decisão — `bloqueado` diz se este está travado,
   * e `aprovacao` traz a última aprovação ativa (com o comentário do gestor na reprovação).
   */
  bloqueado: boolean;
  aprovacao: { status: 'PENDENTE' | 'APROVADA' | 'REPROVADA'; comentario: string | null } | null;
}

export interface Comparacao {
  cotacaoId: string;
  numero: string;
  status: CotacaoStatus;
  pesos: { preco: number; prazo: number; qualidade: number };
  presetNome: string | null;
  /** `true` desde a at-11 (28/09/2026): o fornecedor informa o frete e ele entra na cesta. */
  incluiFrete: boolean;
  itens: ItemComparado[];
  porFornecedor: {
    fornecedorCnpj: string;
    fornecedorNome: string;
    respondeu: boolean;
    itensCotados: number;
    totalItens: number;
    completo: boolean;
    /** Cotou a cotação INTEIRA, não só os itens que recebeu (at-18). */
    cobreCotacaoInteira?: boolean;
    /** Só os itens — o frete fica de fora, para a comparação item a item não distorcer. */
    total: number;
    /** `null` = proposta anterior à at-11, que não declarou frete. Nunca é 0 por omissão. */
    frete: number | null;
    totalComFrete: number;
  }[];
  /*
   * ⚠️ A composição da CESTA não vem do backend, de propósito (at-11): a tela a monta do
   * estado local, com as escolhas que o comprador ainda não salvou. O que vem daqui é o
   * `frete` de cada fornecedor — dado, não composição. Ver a nota em `comparador.service.ts`.
   */
}

export interface DecisaoPayload {
  cotacaoItemId: string;
  propostaItemVencedorId?: string;
  motivoSemCompra?: MotivoSemCompra;
  justificativa?: string;
}

export interface AprovacaoFilaItem {
  cotacaoId: string;
  numero: string;
  compradorNome: string | null;
  placa: string | null;
  aguardandoDesde: string;
  itensDivergentes: number;
  custoDivergencia: number;
  custoDivergenciaPct: number;
}

export interface AprovacaoDetalheItem {
  decisaoItemId: string;
  aprovacaoId: string | null;
  statusAprovacao: 'PENDENTE' | 'APROVADA' | 'REPROVADA' | null;
  descricao: string;
  quantidade: number;
  escolhida: { fornecedorNome: string; totalItem: number; prazoEntregaDias: number } | null;
  menorPrecoPreterido: { fornecedorNome: string; totalItem: number; prazoEntregaDias: number } | null;
  diferenca: number;
  diferencaPct: number;
  seguiuMelhorScore: boolean;
  justificativa: string | null;
}

export interface AprovacaoDetalhe {
  cotacaoId: string;
  numero: string;
  status: CotacaoStatus;
  compradorNome: string | null;
  placa: string | null;
  veiculo: string | null;
  itensDivergentes: AprovacaoDetalheItem[];
}

export interface PedidoGerado {
  id: string;
  fornecedorCnpj: string;
  numeroExterno: string;
  status: 'PENDENTE_ENVIO' | 'ENVIADO' | 'ERRO' | 'CANCELADO';
  simulado: boolean;
  tentativas: number;
  ultimoErro: string | null;
  previsaoDeEntrega: string | null;
  fornecedor?: { razaoSocial: string };
}

/**
 * Imagem anexada à cotação, de USO INTERNO (at-11).
 *
 * 🔴 **O fornecedor não vê nenhuma delas** — nada disto existe na resposta do link dele
 * (decisão do Victor, 28/09/2026).
 *
 * ⚠️ `url` é **assinada e expira** (1 hora): não guardar, não colar em outro lugar, e recarregar
 * a lista quando a página ficar muito tempo aberta. `null` quando a assinatura falhou — a tela
 * mostra o anexo sem prévia, em vez de sumir com ele.
 */
export interface CotacaoAnexo {
  id: string;
  nomeOriginal: string;
  mime: string;
  tamanhoBytes: number;
  cotacaoItemId: string | null;
  itemDescricao: string | null;
  enviadoPor: string | null;
  enviadoEm: string;
  url: string | null;
}

/**
 * Os dois e-mails ao fornecedor depois da decisão (at-11).
 *
 * ⚠️ **`ESPELHO_NF` e `AUTORIZACAO_FATURAMENTO` não são convite.** O painel do convite continua
 * falando só do convite (o backend filtra por finalidade) — quem mistura os dois passa a
 * mostrar o e-mail de faturamento como se fosse o último reenvio do convite.
 */
/** `RESUMO_PROPOSTA` (at-16): o comprador manda ao fornecedor o que ele respondeu. */
export type FinalidadeEmailFornecedor = 'ESPELHO_NF' | 'AUTORIZACAO_FATURAMENTO' | 'RESUMO_PROPOSTA';

/** O desfecho de um envio. `sucesso: false` chega em **200** — o registro fica gravado. */
export interface EnvioDeEmail {
  sucesso: boolean;
  destinatario: string;
  erro: string | null;
}

/** O último envio de cada par (fornecedor, finalidade) — o que a tela mostra ao lado do botão. */
export interface EmailFornecedorEnviado {
  fornecedorCnpj: string;
  finalidade: FinalidadeEmailFornecedor;
  destinatario: string;
  sucesso: boolean;
  erro: string | null;
  em: string;
  /** Nome de quem disparou; cai no id em registro que não guardou o nome. */
  por: string | null;
}

export interface LinhaAlerta {
  pedidoId: string;
  cotacaoNumero: string;
  fornecedorNome: string;
  contato: string | null;
  /** `null` em pedido gerado sem previsão de entrega (lista `semData`). */
  previsao: string | null;
  diasAtraso: number;
  itens: string[];
  placa: string | null;
}

export interface PainelAlertas {
  /**
   * Quatro listas, cobrindo TODO pedido em aberto (11/08/2026). Antes eram duas, e
   * entrega futura ou sem data não aparecia em lista nenhuma.
   */
  proximas: LinhaAlerta[];
  semData: LinhaAlerta[];
  entregasHoje: LinhaAlerta[];
  emAtraso: LinhaAlerta[];
}

export interface GestorCompras {
  userId: string;
  nome: string | null;
}

// ── Tela pública do fornecedor ────────────────────────────────────────────────

export interface CotacaoPublicaValidacao {
  valid: boolean;
  cotacaoNumero: string;
  empresa: string;
  expiresAt: string;
}

export interface CotacaoPublicaItem {
  id: string;
  codigo: string;
  descricao: string;
  quantidade: number;
  unidade: string;
  observacao: string | null;
}

export interface PropostaPublicaItem {
  cotacaoItemId: string;
  /** Posição da oferta no mesmo item (at-16): o fornecedor pode oferecer a peça em várias condições. */
  variante?: number;
  /** Condição da peça (nova original, retirada…), texto da lista vigente (at-16). */
  condicao?: string | null;
  /** Ids das fotos enviadas que acompanham a oferta (envio, at-16). */
  fotoIds?: string[];
  /** As fotos já gravadas na oferta (leitura, at-16). */
  fotos?: { id: string; url: string | null }[];
  cotou: boolean;
  precoUnitario: number | null;
  prazoEntregaDias: number | null;
  marcaFabricante: string | null;
  observacaoItem: string | null;
}

/** Uma condição em que a peça pode ser oferecida (at-16), configurável pela Administração. */
export interface CondicaoPecaOpcao {
  nome: string;
  exigeFoto: boolean;
}

/** O conteúdo da cotação para o fornecedor. Sem senha desde a at-17 (ADR 0015 do Portal). */
export type RespostaVerificacaoPublica = CotacaoPublica;

export interface CotacaoPublica {
  numero: string;
  empresa: string;
  expiresAt: string;
  veiculo: string | null;
  placa: string | null;
  statusConvite: ConviteStatus;
  aceitandoRespostas: boolean;
  /** O prazo de respostas (at-16): passado ele, a resposta segue aceita, marcada como fora do prazo. */
  prazoRespostaAte?: string;
  /** Foto do veículo escolhida pelo comprador (at-16), URL assinada. */
  fotoVeiculoUrl?: string | null;
  /** Fotos que o comprador escolheu mostrar (at-17: dano, oficina externa). Com item, aparecem também nele. */
  fotosVeiculo?: { id: string; cotacaoItemId: string | null; url: string | null }[];
  /** Condições ATIVAS da peça (at-16). Vazia = o formulário não pede condição. */
  condicoesOpcoes?: CondicaoPecaOpcao[];
  itens: CotacaoPublicaItem[];
  propostaAtual: {
    versao: number;
    enviadaEm: string;
    condicaoPagamento: string | null;
    /**
     * Frete informado pelo fornecedor (at-11). `null` em proposta anterior ao campo — o
     * formulário deixa vazio nesse caso, nunca `0`.
     */
    frete?: number | null;
    observacao: string | null;
    /** O que o fornecedor marcou (at-10) — usado para remarcar na reedição. */
    formasPagamento?: string[];
    itens: PropostaPublicaItem[];
  } | null;
}

// ── Notificações do módulo (at-06) ────────────────────────────────────────────

/**
 * Aviso do Portal para o usuário logado.
 *
 * O backend gravava esses avisos desde o MVP e **nenhuma tela os lia** (`BUG-V02`): o
 * gestor não sabia que tinha aprovação esperando, o comprador não sabia de proposta
 * recebida nem de reprovação. O painel da lista de cotações é o leitor.
 */
export type NotificacaoCotacaoTipo =
  | 'PROPOSTA_RECEBIDA'
  | 'APROVACAO_PENDENTE'
  | 'APROVACAO_DECIDIDA'
  | 'CONVITE_BLOQUEADO'
  | 'PEDIDOS_GERADOS'
  | 'PEDIDOS_COM_ERRO'
  | 'TENTATIVA_LINK_INVALIDO'
  | 'PRAZO_PROXIMO'
  | 'COTACAO_CANCELADA'
  | 'ENTREGAS_DO_DIA'
  /*
   * at-06 do Cilia (17/08/2026): complemento do orçamento tirou/reduziu peça desta cotação.
   * Único tipo cuja emissão parte de OUTRO módulo — quem detecta é a tela do Cilia, que
   * registra o aviso pela rota do Portal com o JWT do usuário (ADR 0014).
   */
  | 'COMPLEMENTO_ALTEROU_PECAS';

export interface NotificacaoCotacao {
  id: string;
  tipo: NotificacaoCotacaoTipo;
  titulo: string;
  mensagem: string;
  /** Nulo nos avisos que cruzam várias cotações (ex.: resumo diário de entregas). */
  cotacaoId: string | null;
  lida: boolean;
  createdAt: string;
}

export interface NotificacoesResposta {
  data: NotificacaoCotacao[];
  /** Quantas existem no filtro — é o que o "Carregar mais (N de M)" compara. */
  total: number;
  /** Total não lido do usuário, sempre do conjunto inteiro — é o badge do sino. */
  naoLidas: number;
  page: number;
  limit: number;
}

// ── Rótulos e cores (compartilhados entre as telas) ───────────────────────────

export const COTACAO_STATUS_LABEL: Record<CotacaoStatus, string> = {
  RASCUNHO: 'Rascunho',
  ENVIADA: 'Enviada',
  EM_RESPOSTA: 'Em resposta',
  EM_DECISAO: 'Em decisão',
  AGUARDANDO_APROVACAO: 'Aguardando aprovação',
  GERANDO_PEDIDOS: 'Gerando pedidos',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

export const CONVITE_STATUS_LABEL: Record<ConviteStatus, string> = {
  CRIADO: 'Criado',
  ENVIADO: 'Enviado',
  VISUALIZADO: 'Visualizou',
  RESPONDIDO: 'Respondeu',
  DECLINADO: 'Declinou',
  REVOGADO: 'Revogado',
  BLOQUEADO_REVISAO: 'Bloqueado',
};

